import React, { useState, createContext, useCallback, useMemo, useRef } from 'react';

const context = {};

export const TemporaryChatContext = createContext(context);

const DEFAULT_AGENT = {
  label: 'Kai',
  value: '7e1cf244-9e8e-4782-981d-24cae676f7k1',
  icon: 'https://feedalabs-agent-icon.s3.eu-north-1.amazonaws.com/kai_3dc7591ed4d94066b49140b4eeb5e496.jpg',
};

export const TemporaryChatContextProvider = props => {
  const [selectedAgent, setSelectedAgent] = useState(() => {
    const saved = localStorage.getItem('agent');
    return saved ? JSON.parse(saved) : DEFAULT_AGENT;
  });

  const [chatMessages, setChatmessages] = useState([]);
  const [workflowChatMessages, setWorkflowChatmessages] = useState([]);
  const [toolImages, setToolImages] = useState([]);
  const [chatLoaderMessages, setChatLoaderMessages] = useState({});
  const [webSearch, setWebSearch] = useState(false);
  const [tempText, setTempText] = useState('');
  const [sessionID, setSessionID] = useState(null);
  const [sessionAgent, setSessionAgent] = useState(DEFAULT_AGENT);
  const isActiveRef = useRef(true);

  const toggleRef = useCallback(value => {
    isActiveRef.current = value;
  }, []);

  const fakeAiLoader = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isLoading: true }];
    });
  }, []);

  const fakeAiLoaderClear = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isLoading: false }];
    });
  }, []);

  const updateUserChat = useCallback(message => {
    const userMessage = {
      human: message,
      ai: '',
      isStreaming: true,
      source: [],
      intermediate_steps: [
        {
          tool_name: 'thinking...',
          content: message,
          isLoading: true,
        },
      ],
    };
    setChatmessages(prev => [...prev, userMessage]);
  }, []);

  const updateBotChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [
        ...prev.slice(0, -1),
        { ...last, ai: (last.ai || '') + newChunk, isLoading: false, isStreaming: true },
      ];
    });
  }, []);

  const finishStream = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isStreaming: false, isLoading: false }];
    });
  }, []);

  const updateDelegateBotChat = useCallback(delegateAgent => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [...prev, { ...last, delegate_agent: delegateAgent, ai: ' ', isLoading: false }];
    });
  }, []);

  const updateStepsChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const existingSteps = Array.isArray(last.intermediate_steps) ? last.intermediate_steps : [];
      const silencedSteps = existingSteps.map(step => ({ ...step, isLoading: false }));
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          intermediate_steps: [...silencedSteps, { ...newChunk, isLoading: true }],
          isLoading: false,
        },
      ];
    });
  }, []);

  const updateStepsResultsChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const steps = Array.isArray(last.intermediate_steps) ? last.intermediate_steps : [];
      const stepExists = steps.some(step => step.tool_call_id === newChunk?.tool_call_id);
      const updatedSteps = stepExists
        ? steps.map(step => (step.tool_call_id === newChunk?.tool_call_id ? newChunk : step))
        : [...steps, newChunk];
      return [...prev.slice(0, -1), { ...last, intermediate_steps: updatedSteps, isLoading: false }];
    });
  }, []);

  const updateAllStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (!Array.isArray(last?.intermediate_steps)) return prev;
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          intermediate_steps: last.intermediate_steps.map(step => ({ ...step, isLoading: false })),
          isLoading: false,
        },
      ];
    });
  }, []);

  const updateThinkingStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const [first, ...rest] = prev;
      return [{ ...first, isLoading: false }, ...rest];
    });
  }, []);

  const updateWorkflowUserChat = useCallback(message => {
    setWorkflowChatmessages(prev => [...prev, { human: message, ai: '', source: [] }]);
  }, []);

  const updateWorkflowBotChat = useCallback(newChunk => {
    setWorkflowChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, ai: (last.ai || '') + newChunk, isLoading: false }];
    });
  }, []);

  const updateToolImages = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, images: newChunk?.search_results, isLoading: false }];
    });
  }, []);

  const updateChatLoaderMessage = useCallback(newChunk => {
    setChatLoaderMessages({ newChunk, isLoading: false });
  }, []);

  const updateChatSource = useCallback(newSources => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const sources = Array.isArray(newSources) ? newSources : [];
      const websearchSources = sources.filter(src => src.type === 'google_search');
      const fileSources = sources.filter(src => src.type === 'file');
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          source: websearchSources.length > 0 ? websearchSources : [],
          files: fileSources.length > 0 ? fileSources : [],
        },
      ];
    });
  }, []);

  const updateWebSearch = useCallback(() => {
    setWebSearch(prev => !prev);
  }, []);

  const updateAgent = useCallback(agent => {
    localStorage.setItem('agent', JSON.stringify(agent));
    setSelectedAgent(agent);
  }, []);

  const contextValue = useMemo(
    () => ({
      chatMessages,
      workflowChatMessages,
      chatLoaderMessages,
      updateWorkflowBotChat,
      updateWorkflowUserChat,
      updateStepsChat,
      updateAllStepsLoading,
      updateStepsResultsChat,
      setSessionID,
      sessionID,
      setChatmessages,
      toolImages,
      setToolImages,
      updateToolImages,
      updateUserChat,
      updateBotChat,
      finishStream,
      updateChatLoaderMessage,
      updateChatSource,
      fakeAiLoader,
      fakeAiLoaderClear,
      webSearch,
      updateWebSearch,
      setTempText,
      tempText,
      selectedAgent,
      setSelectedAgent,
      updateAgent,
      setSessionAgent,
      sessionAgent,
      toggleRef,
      isActiveRef,
      updateDelegateBotChat,
      updateThinkingStepsLoading,
    }),
    [
      chatMessages,
      workflowChatMessages,
      chatLoaderMessages,
      updateWorkflowBotChat,
      updateWorkflowUserChat,
      updateStepsChat,
      updateAllStepsLoading,
      updateStepsResultsChat,
      sessionID,
      toolImages,
      updateToolImages,
      updateUserChat,
      updateBotChat,
      finishStream,
      updateChatLoaderMessage,
      updateChatSource,
      fakeAiLoader,
      fakeAiLoaderClear,
      webSearch,
      updateWebSearch,
      tempText,
      selectedAgent,
      updateAgent,
      sessionAgent,
      toggleRef,
      updateDelegateBotChat,
      updateThinkingStepsLoading,
    ],
  );

  return <TemporaryChatContext.Provider value={contextValue}>{props.children}</TemporaryChatContext.Provider>;
};
